import React, { Component } from 'react';
import { connect } from 'react-redux';
import { hashHistory } from 'react-router';
import MemberSelectionScreen from './Components/MemberSelectionScreen';
import ManageBenefitsModal from './Components/ManageBenefitsModal';
import SummaryScreen from './Components/SummaryScreen';
import AmendmentProgressDialog from '../shared/Components/AmendmentProgressDialog';
import { processManageBenefitsAmendment } from './utils/amendmentApi';
import { getAllProducts } from '../../../../../lib/requests.es6';
import { isCoreBenefit } from './utils/benefitHelpers';
import { addAmendmentTracking, findMatchingOldBenefit } from '../shared/utils/amendmentTrackingHelper';

class ManageBenefits extends Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 'select', // 'select', 'summary'
      members: [],
      originalMembers: [],
      selectedMember: null,
      showBenefitsModal: false,
      // Amendment progress tracking
      steps: { quote: 'pending', swap: 'pending', note: 'pending' },
      quoteError: null,
      swapError: null,
      noteError: null,
      showSuccessDialog: false,
      products: props.products || [],
      loadingProducts: false
    };
  }

  componentDidMount() {
    this.initializeMembers();

    const { products } = this.props;
    // Fetch products if not available from Redux
    if (!products || products.length === 0) {
      this.fetchProducts();
    }
  }

  fetchProducts = async () => {
    this.setState({ loadingProducts: true });

    try {
      const response = await getAllProducts();
      const products = response.data || [];

      this.setState({
        products,
        loadingProducts: false
      });
    } catch (error) {
      console.error('Failed to fetch products:', error);
      this.setState({
        loadingProducts: false,
        error: 'Failed to load product information. Please try again.'
      });
    }
  };

  initializeMembers = () => {
    const { policy } = this.props;

    // Extract members from policy_schedule_entries
    if (!policy || !policy.quotation || !policy.quotation.policy_schedule_entries) {
      this.setState({ members: [], originalMembers: [] });
      return;
    }

    const members = policy.quotation.policy_schedule_entries.map(entry => {
      // Check if entry has a nested dependant structure or flat structure
      if (entry.dependant) {
        // Nested structure (from policy API response)
        return {
          ...entry.dependant,
          benefits: entry.benefits || [],
          premium_amount_in_cents: entry.premium_amount_in_cents || 0,
          uuid: entry.dependant.additional_details && entry.dependant.additional_details.uuid
            ? entry.dependant.additional_details.uuid
            : entry.dependant.guid,
          type: entry.type || (entry.dependant.additional_details && entry.dependant.additional_details.memberType)
        };
      } else {
        // Flat structure (already in correct format)
        return {
          ...entry,
          uuid: entry.uuid || entry.guid
        };
      }
    });

    this.setState({
      members: JSON.parse(JSON.stringify(members)),
      originalMembers: JSON.parse(JSON.stringify(members))
    });
  };

  handleManageBenefits = (member) => {
    this.setState({
      selectedMember: member,
      showBenefitsModal: true
    });
  };

  handleSaveBenefits = (member, selectedBenefits) => {
    const { members } = this.state;
    const { policy } = this.props;
    const oldQuotation = policy.quotation; // For amendment tracking
    const currentPlan = oldQuotation && oldQuotation.additional_details
      ? oldQuotation.additional_details.primary_plan
      : null;

    // Helper to get benefit premium from various possible locations
    const getBenefitPremium = (benefit) => {
      return benefit.premium_amount ||
             (benefit.additional_details && benefit.additional_details.premium_amount) ||
             0;
    };

    // Update the member with new benefits
    const updatedMembers = members.map(m => {
      if (m.uuid === member.uuid) {
        // Get original member to calculate base premium
        const originalMember = this.state.originalMembers.find(om => om.uuid === m.uuid);
        const originalBenefits = originalMember && originalMember.benefits ? originalMember.benefits : [];

        // Create a set of original benefit identifiers for comparison
        const originalBenefitIds = new Set();
        if (originalMember && originalMember.benefits) {
          originalMember.benefits.forEach(b => {
            // Use UUID as primary identifier
            if (b.uuid) {
              originalBenefitIds.add(b.uuid);
            }
            // Also create composite key for name+amount matching
            const benefitName = (b.name || b.type || '').toLowerCase();
            const benefitAmount = b.cover_amount_in_cents || b.benefit_amount_in_cents || 0;
            const benefitPremium = getBenefitPremium(b);
            if (benefitName) {
              originalBenefitIds.add(`${benefitName}:${benefitAmount}:${benefitPremium}`);
            }
          });
        }

        // Process selected benefits: set waiting_period_in_days to 150 for newly added benefits only
        const processedBenefits = selectedBenefits.map(benefit => {
          // Check if this is a new benefit
          const benefitUuid = benefit.uuid;
          const benefitName = (benefit.name || benefit.type || '').toLowerCase();
          const benefitAmount = benefit.cover_amount_in_cents || benefit.benefit_amount_in_cents || 0;
          const benefitPremium = getBenefitPremium(benefit);
          const compositeKey = `${benefitName}:${benefitAmount}:${benefitPremium}`;

          const isNewBenefit = !originalBenefitIds.has(benefitUuid) && !originalBenefitIds.has(compositeKey);

          // If new benefit, set waiting period to 150 days
          if (isNewBenefit) {
            return {
              ...benefit,
              waiting_period_in_days: 150
            };
          }

          // Existing benefit - check if it changed and add tracking
          const benefitType = benefit.type || benefit.name;
          const oldBenefit = findMatchingOldBenefit(originalBenefits, benefitType);

          if (oldBenefit && currentPlan) {
            // Add amendment tracking if benefit amount changed
            return addAmendmentTracking(benefit, oldBenefit, oldQuotation, currentPlan, 'manage_benefits');
          }

          // Existing benefit unchanged, keep as is
          return benefit;
        });

        // Calculate original additional benefits premium (non-core benefits only)
        const originalAdditionalBenefits = originalMember && originalMember.benefits
          ? originalMember.benefits.filter(b => !isCoreBenefit(b))
          : [];
        const originalAdditionalPremium = originalAdditionalBenefits.reduce((sum, b) => sum + getBenefitPremium(b), 0);

        // Calculate new additional benefits premium (non-core benefits only)
        const newAdditionalBenefits = processedBenefits.filter(b => !isCoreBenefit(b));
        const newAdditionalPremium = newAdditionalBenefits.reduce((sum, b) => sum + getBenefitPremium(b), 0);

        // Base premium (nuclear premium including core benefits) = original total - original additional
        const originalTotalPremium = originalMember && originalMember.premium_amount_in_cents
          ? originalMember.premium_amount_in_cents
          : 0;
        const basePremium = originalTotalPremium - originalAdditionalPremium;

        // New total premium = base premium + new additional premium
        const totalPremium = basePremium + newAdditionalPremium;

        return {
          ...m,
          benefits: processedBenefits,
          premium_amount_in_cents: totalPremium
        };
      }
      return m;
    });

    this.setState({
      members: updatedMembers,
      showBenefitsModal: false,
      selectedMember: null
    });
  };

  handleCloseBenefitsModal = () => {
    this.setState({
      showBenefitsModal: false,
      selectedMember: null
    });
  };

  handleProceedToSummary = () => {
    this.setState({ step: 'summary' });
  };

  handleBackToSelect = () => {
    this.setState({ step: 'select' });
  };

  handleConfirmAmendment = async () => {
    const { policy } = this.props;
    const { members } = this.state;

    // Reset step statuses
    this.setState({
      steps: { quote: 'pending', swap: 'pending', note: 'pending' },
      quoteError: null,
      swapError: null,
      noteError: null,
      showSuccessDialog: false
    });

    try {
      const result = await processManageBenefitsAmendment(
        policy,
        members,
        (step, status) => {
          // Update step status (loading, success, error)
          this.setState(prevState => ({
            steps: { ...prevState.steps, [step]: status }
          }));
        }
      );

      // Amendment succeeded (even if note failed)
      this.setState({
        showSuccessDialog: true,
        noteError: result.noteError || null
      });
    } catch (error) {
      console.error('Error processing manage benefits amendment:', error);
      const errorMessage = error.message || 'Failed to process benefit amendment. Please try again.';

      // Determine which step failed
      this.setState(prevState => {
        const quoteError = prevState.steps.quote === 'error' ? errorMessage : null;
        const swapError = prevState.steps.swap === 'error' ? errorMessage : null;

        return { quoteError, swapError };
      });
    }
  };

  handleSuccess = () => {
    const { policy } = this.props;
    this.setState({ showSuccessDialog: false });
    // Navigate to policy processing page
    hashHistory.push(`admin/policy-processing/${policy.policy_number}`);
  };

  handleRetryAfterError = () => {
    this.setState({
      steps: { quote: 'pending', swap: 'pending', note: 'pending' },
      quoteError: null,
      swapError: null,
      noteError: null
    });
  };

  render() {
    const { policy } = this.props;
    const {
      step,
      members,
      originalMembers,
      selectedMember,
      showBenefitsModal,
      steps,
      quoteError,
      swapError,
      noteError
    } = this.state;

    return (
      <div>
        {step === 'select' && (
          <MemberSelectionScreen
            policy={policy}
            members={members}
            originalMembers={originalMembers}
            onManageBenefits={this.handleManageBenefits}
            onCancel={this.props.onCancel}
            onContinue={this.handleProceedToSummary}
          />
        )}

        {step === 'summary' && (
          <SummaryScreen
            policy={policy}
            originalMembers={originalMembers}
            updatedMembers={members}
            onBack={this.handleBackToSelect}
            onConfirm={this.handleConfirmAmendment}
          />
        )}

        <ManageBenefitsModal
          open={showBenefitsModal}
          member={selectedMember}
          policy={policy}
          products={this.state.products}
          onSave={this.handleSaveBenefits}
          onClose={this.handleCloseBenefitsModal}
        />

        <AmendmentProgressDialog
          steps={steps}
          quoteError={quoteError}
          swapError={swapError}
          noteError={noteError}
          successMessage="Benefits updated successfully. The policy has been amended."
          onViewPolicy={this.handleSuccess}
          onRetry={this.handleRetryAfterError}
        />
      </div>
    );
  }
}

export default connect((state) => ({
  products: state.customerScreen ? state.customerScreen.products : []
}))(ManageBenefits);
